/**
 * Product Drawer Web Component
 * 
 * A drawer component that expands/collapses product form content
 * Used for the "More" section on product pages
 * 
 * Usage:
 * <product-drawer>
 *   <button data-drawer-trigger>Toggle</button>
 *   <div data-drawer-content>
 *     <!-- drawer content -->
 *   </div>
 * </product-drawer>
 * 
 * To open: productDrawer.open()
 * To close: productDrawer.close()
 * To toggle: productDrawer.toggle()
 */
class ProductDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;

    // Bind event handlers once in constructor
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
  }

  connectedCallback() {
    this.content = this.querySelector('[data-drawer-content]');
    this.trigger = this.querySelector('[data-drawer-trigger]');
    this.triggerText = this.querySelector('[data-drawer-trigger-text]');

    if (!this.content) {
      console.warn('ProductDrawer: Missing required [data-drawer-content] element');
      return;
    }

    if (!this.trigger) {
      console.warn('ProductDrawer: Missing required [data-drawer-trigger] element');
      return;
    }

    // Ensure drawer starts closed
    this.setAttribute('aria-expanded', 'false');
    
    // Use requestAnimationFrame to ensure CSS is applied before init
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    this.setupAccessibility();
    this.setupEventListeners();
    this.setupStyles();
  }

  setupAccessibility() {
    // Set ARIA attributes for accessibility
    this.setAttribute('role', 'region');
    this.setAttribute('aria-expanded', 'false');
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', this.content.id || 'product-drawer-content');
    }
  }

  setupEventListeners() {
    // Escape key to close
    document.addEventListener('keydown', this.handleEscapeKey);
    
    // Trigger button
    if (this.trigger) {
      this.trigger.addEventListener('click', this.handleTriggerClick);
    }
  }

  setupStyles() {
    // Add class for CSS targeting
    this.classList.add('product-drawer');
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  handleTriggerClick(e) {
    e.preventDefault();
    this.toggle();
  }

   open() {
     if (this.isOpen) return;

     this.isOpen = true;
     this.setAttribute('aria-expanded', 'true');
     this.classList.add('is-open');
     
     // Find the product form container
     const formContainer = this.closest('.product-form-container');
     
     if (formContainer) {
       // Add class to container for CSS targeting
       formContainer.classList.add('drawer-open');
       
       // Get header height from CSS variable or use default
       const headerHeightValue = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
       const headerHeight = headerHeightValue ? parseInt(headerHeightValue) : 65;
       
       // Calculate target height - full viewport minus header
       const targetHeight = window.innerHeight - headerHeight;
       
       // Get button
       const button = formContainer.querySelector('.product-add-to-cart');
       const isMobile = window.innerWidth <= 768;
       
       if (button && !isMobile) {
         // On desktop, use absolute positioning
         button.style.position = 'absolute';
         button.style.bottom = 'var(--page-margin)';
         button.style.left = 'var(--page-margin)';
         button.style.right = 'var(--page-margin)';
         button.style.width = 'calc(100% - var(--page-margin) * 2)';
       }
       
       // Set container to full height
       formContainer.style.height = `${targetHeight}px`;
       formContainer.style.maxHeight = 'none';
     }
     
     if (this.trigger) {
       this.trigger.setAttribute('aria-expanded', 'true');
     }

     // Update trigger text
     if (this.triggerText) {
       this.triggerText.textContent = 'Less';
     }

     // Dispatch custom event
     this.dispatchEvent(new CustomEvent('drawer:open', {
       bubbles: true,
       detail: { drawer: this }
     }));
   }

   close() {
     if (!this.isOpen) return;

     this.isOpen = false;
     this.setAttribute('aria-expanded', 'false');
     
     // Find the product form container
     const formContainer = this.closest('.product-form-container');
     if (formContainer) {
       // Remove class from container
       formContainer.classList.remove('drawer-open');
       
       const button = formContainer.querySelector('.product-add-to-cart');
       const isMobile = window.innerWidth <= 768;
       
       // Reset container inline styles
       formContainer.style.height = '';
       formContainer.style.maxHeight = '';
       formContainer.style.bottom = '';
       formContainer.style.top = '';
       
       // Remove is-open class
       this.classList.remove('is-open');
       
       // Clean up button inline styles (only for desktop)
       if (button && !isMobile) {
         button.style.position = '';
         button.style.bottom = '';
         button.style.left = '';
         button.style.right = '';
         button.style.width = '';
       }
     } else {
       this.classList.remove('is-open');
     }
     
     if (this.trigger) {
       this.trigger.setAttribute('aria-expanded', 'false');
     }

     // Update trigger text
     if (this.triggerText) {
       this.triggerText.textContent = 'More';
     }

     // Dispatch custom event
     this.dispatchEvent(new CustomEvent('drawer:close', {
       bubbles: true,
       detail: { drawer: this }
     }));
   }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscapeKey);
  }
}

customElements.define('product-drawer', ProductDrawer);

